import onelps from '../api';
import { metrics } from '../metrics-config';
import { getQueryParam, MARKETING_QUERY_PARAMS, getAllQueryParams, FRIEND_INVITE_QUERY_PARAMS } from './url';
import {
	isValidationErrorResponse, isResponseWithSuggestions,
	handleNicknameValidity, handleEmailValidity, handlePasswordValidity, handleNicknameSuggestions,
} from './validation/common';

export const REGISTRATION_ERROR_CODES = {
	TERMS_AND_CONDITIONS: 'be.api.auth.termsAndConditions_blank',
};

const handleOtherErrors = (otherErrors, termsDispatch, dispatch) => {
	if (!otherErrors || !Array.isArray(otherErrors)) {
		return;
	}

	const termsAndConditionsErrorIndex =
		otherErrors.findIndex(error => error.code === REGISTRATION_ERROR_CODES.TERMS_AND_CONDITIONS);

	if (termsAndConditionsErrorIndex !== -1) {
		const termsAndConditionsError = otherErrors[termsAndConditionsErrorIndex];
		termsDispatch({ type: 'SET_TC_ERROR', payload: termsAndConditionsError.message });
		termsDispatch({ type: 'SHOW_TC_ERROR' });
		otherErrors.splice(termsAndConditionsErrorIndex, 1);
	}

	if (otherErrors.length === 1) {
		dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: otherErrors[0].message });
		dispatch({ type: 'SHOW_SERVER_ERROR' });
	} else if (otherErrors.length > 1) {
		dispatch({ type: 'SET_SERVER_MESSAGES', payload: otherErrors.map(error => error.message) });
	}
};

const handleRedirectResponse = (regResult) => {
	const redirectUrl = regResult.redirectUrl;
	const playerId = regResult.playerId;
	const callbackFn = () => {
		window.location.href = redirectUrl;
	};

	if (playerId && metrics) {
		const clickId = getQueryParam(MARKETING_QUERY_PARAMS.clickId);
		const queryParams = getAllQueryParams();

		metrics.collectRegisterSuccessMetrics(playerId, clickId, queryParams, callbackFn);
	}
};

const isRegisterResponseSuccess = (response) => response.playerId && response.redirectUrl;

export const registerWithAllFields = async function(
	locale,
	dispatchFn, nicknameDispatchFn, passwordDispatchFn, emailDispatchFn, termsDispatchFn,
	nickname, password, email, termsAndConditions, thirdPartyTrackingAccepted, race,
	onFailureCallbackFn,
) {
	try {
		const requestData = {
			type: 'full',
			nickname: nickname,
			password: password,
			email: email,
			acceptTermsAndConditions: termsAndConditions,
			acceptThirdPartyPixels: thirdPartyTrackingAccepted,
		};

		if (race) {
			requestData.onyxRaceOption = race;
		}
		const friendInvite = getQueryParam(FRIEND_INVITE_QUERY_PARAMS.friendInvite);
		if (friendInvite) {
			requestData.friendInvite = friendInvite;
		}
		const regResult = await onelps.actions.register(requestData, locale);

		if (isValidationErrorResponse(regResult)) {
			handleNicknameValidity(regResult.errors.nickname, nicknameDispatchFn);
			handlePasswordValidity(regResult.errors.password, passwordDispatchFn);
			handleEmailValidity(regResult.errors.email, emailDispatchFn);
			handleOtherErrors(regResult.errors.other, termsDispatchFn, dispatchFn);
		}

		if (isResponseWithSuggestions(regResult)) {
			handleNicknameSuggestions(regResult, nicknameDispatchFn);
		}

		if (isRegisterResponseSuccess(regResult)) {
			handleRedirectResponse(regResult);
		}
	} catch (e) {
		dispatchFn({ type: 'SHOW_SERVER_ERROR' });
		onFailureCallbackFn && onFailureCallbackFn();
	}
};

export const registerWithoutEmail = async function(
	locale,
	dispatchFn, nicknameDispatchFn, passwordDispatchFn, termsDispatchFn,
	nickname, password, termsAndConditions, thirdPartyTrackingAccepted, race,
	onFailureCallbackFn,
) {
	try {
		const requestData = {
			type: 'no_email',
			nickname: nickname,
			password: password,
			acceptTermsAndConditions: termsAndConditions,
			acceptThirdPartyPixels: thirdPartyTrackingAccepted,
		};

		if (race) {
			requestData.onyxRaceOption = race;
		}

		const friendInvite = getQueryParam(FRIEND_INVITE_QUERY_PARAMS.friendInvite);
		if (friendInvite) {
			requestData.friendInvite = friendInvite;
		}

		const regResult = await onelps.actions.register(requestData, locale);

		if (isValidationErrorResponse(regResult)) {
			handleNicknameValidity(regResult.errors.nickname, nicknameDispatchFn);
			handlePasswordValidity(regResult.errors.password, passwordDispatchFn);
			handleOtherErrors(regResult.errors.other, termsDispatchFn, dispatchFn);
		}

		if (isResponseWithSuggestions(regResult)) {
			handleNicknameSuggestions(regResult, nicknameDispatchFn);
		}

		if (isRegisterResponseSuccess(regResult)) {
			handleRedirectResponse(regResult);
		}
	} catch (e) {
		dispatchFn({ type: 'SHOW_SERVER_ERROR' });
		onFailureCallbackFn && onFailureCallbackFn();
	}
};

export const registerWithoutNickname = async function(
	locale,
	dispatchFn, emailDispatchFn, passwordDispatchFn, termsDispatchFn,
	email, password, termsAndConditions, thirdPartyTrackingAccepted, race,
	onFailureCallbackFn,
) {
	try {
		const requestData = {
			type: 'no_nickname',
			email: email,
			password: password,
			acceptTermsAndConditions: termsAndConditions,
			acceptThirdPartyPixels: thirdPartyTrackingAccepted,
		};

		if (race) {
			requestData.onyxRaceOption = race;
		}

		const friendInvite = getQueryParam(FRIEND_INVITE_QUERY_PARAMS.friendInvite);
		if (friendInvite) {
			requestData.friendInvite = friendInvite;
		}

		const regResult = await onelps.actions.register(requestData, locale);

		if (isValidationErrorResponse(regResult)) {
			handleEmailValidity(regResult.errors.email, emailDispatchFn);
			handlePasswordValidity(regResult.errors.password, passwordDispatchFn);
			handleOtherErrors(regResult.errors.other, termsDispatchFn, dispatchFn);
		}

		if (isRegisterResponseSuccess(regResult)) {
			handleRedirectResponse(regResult);
		}
	} catch (e) {
		dispatchFn({ type: 'SHOW_SERVER_ERROR' });
		onFailureCallbackFn && onFailureCallbackFn();
	}
};

export const registerWithOneClick = async function(
	locale,
	dispatchFn,
	termsDispatchFn,
	termsAndConditions,
	thirdPartyTrackingAccepted,
	onFailureCallbackFn,
) {
	try {
		const requestData = {
			type: 'one_click',
			password: null,
			acceptTermsAndConditions: termsAndConditions,
			acceptThirdPartyPixels: thirdPartyTrackingAccepted,
		};

		const friendInvite = getQueryParam(FRIEND_INVITE_QUERY_PARAMS.friendInvite);
		if (friendInvite) {
			requestData.friendInvite = friendInvite;
		}

		const regResult = await onelps.actions.register(requestData, locale);

		if (isRegisterResponseSuccess(regResult)) {
			handleRedirectResponse(regResult);
		}
	} catch (e) {
		dispatchFn({ type: 'SHOW_SERVER_ERROR' });
		onFailureCallbackFn && onFailureCallbackFn();
	}
};
